import type { FC, PropsWithChildren } from 'react';
import {
    Card,
    Col,
    Grid,
    MatchMedia,
    Row,
    SkeletonAnimatedContainer,
    SkeletonAvatar,
    SkeletonLine,
    Spinner,
    StripeActionButton,
    StripeHeader,
    StructuredInfo,
    StructuredInfoItem,
} from '@george-labs.com/design-system';

import { useGlobalColorVariants, useStripeHeaderStickyOffset } from '@shared/core-ui-layout';

import styles from '../MainLayout.module.scss';

interface MenuProps {
    width: number;
}

const SideMenuPlaceholder: FC<MenuProps> = ({ width }) => (
    <div className="px-3 mt-2 mb-1">
        <SkeletonLine level={4} animated percentageWidth={width} className="pt-0" />
    </div>
);

interface Props {
    'data-cy'?: string;
}

export const ProductLayoutSkeleton: FC<PropsWithChildren<Props>> = ({
    children = <Spinner center />,
    'data-cy': dataCy = 'product-layout-skeleton-loader',
}) => {
    const { color } = useGlobalColorVariants();
    const stickyOffset = useStripeHeaderStickyOffset();

    return (
        <div data-cy={dataCy} className={styles['products-main-layout']}>
            <StripeHeader
                color={color}
                center={
                    <SkeletonLine level={2} animated percentageWidth={50} className="m-auto pt-0" />
                }
                right={
                    <StripeActionButton
                        key="product-layout-skeleton-stripe-action-button"
                        labelClassName="w-75"
                        className="w-75"
                        disabled
                    >
                        <SkeletonLine
                            level={4}
                            animated
                            percentageWidth={100}
                            className="pt-0 mt-2"
                        />
                    </StripeActionButton>
                }
                stickyOffset={stickyOffset}
                sticky
                className={styles['layout-stripe']}
            />
            <Grid data-cy="content-container" className="mt-4" fluid={false}>
                <Row>
                    <MatchMedia min={MatchMedia.TIER.LG}>
                        <Col lg={3}>
                            <SkeletonAnimatedContainer className="h-auto">
                                <Card className="mt-0 mb-3">
                                    <StructuredInfo
                                        avatar={
                                            <SkeletonAvatar
                                                size={SkeletonAvatar.SIZE.SIZE_48}
                                                animated
                                            />
                                        }
                                        color={color}
                                        header={
                                            <>
                                                <StructuredInfoItem faded>
                                                    <SkeletonLine
                                                        percentageWidth={75}
                                                        level={6}
                                                        animated
                                                        className="mt-2 pt-0"
                                                    />
                                                </StructuredInfoItem>
                                                <StructuredInfoItem>
                                                    <SkeletonLine
                                                        percentageWidth={100}
                                                        level={5}
                                                        animated
                                                        className="pt-0"
                                                    />
                                                </StructuredInfoItem>
                                            </>
                                        }
                                        alignment={StructuredInfo.ALIGNMENT.RIGHT}
                                    >
                                        <StructuredInfoItem>
                                            <SkeletonLine
                                                percentageWidth={100}
                                                level={5}
                                                animated
                                                className="mt-1 pt-0"
                                            />
                                        </StructuredInfoItem>
                                        <StructuredInfoItem>
                                            <SkeletonLine
                                                percentageWidth={50}
                                                level={5}
                                                animated
                                                className="mt-1 pt-0"
                                            />
                                        </StructuredInfoItem>
                                    </StructuredInfo>
                                </Card>
                            </SkeletonAnimatedContainer>
                            <SideMenuPlaceholder width={95} />
                            <SideMenuPlaceholder width={75} />
                            <SideMenuPlaceholder width={55} />
                            <SideMenuPlaceholder width={85} />
                            <SideMenuPlaceholder width={100} />
                            <SideMenuPlaceholder width={65} />
                        </Col>
                    </MatchMedia>

                    <Col xs={12} lg={9}>
                        <main>{children}</main>
                    </Col>
                </Row>
            </Grid>
        </div>
    );
};
